import { AutoComplete, Button, Checkbox, Form, Input, Radio, Select, Space } from "antd";
import FormItem from "antd/es/form/FormItem";
import businessAreaDataDomains from "common/data/businessAreaDataDomains";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import mergeClassNames from "common/mergeClassNames";
import { DomainDataPreviewList, FileDragger } from "components";
import { DataTagSelect } from "containers";
import React, { Component } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;

class ManageBusinessAreaData extends Component {
    constructor(props) {
        super(props);
        this.businessAreaDataDefault = {
            usedForTagging: false,
        };
        this.formRef = React.createRef();
        this.state = {
            readOnlyView: this.props.action === "view",
        };
        this.emptyObject = {};
    }

    componentWillMount() {}

    componentDidMount() {
        if (this.props.businessAreaData) {
            this.formRef.current.setFieldsValue({ ...this.props.businessAreaData });
        } else {
            this.selectDefaultBusinessArea();
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.businessAreaList !== prevProps.businessAreaList && this.props.businessAreaList.length > 0) {
            this.selectDefaultBusinessArea();
        }
    }

    selectDefaultBusinessArea = () => {
        if (this.props.businessAreaList.length > 0) {
            if (this.formRef.current) {
                let selectedBusinessAreaId = this.formRef.current.getFieldValue(["businessAreaId"]);
                if (!selectedBusinessAreaId) {
                    this.formRef.current.setFieldsValue({ businessAreaId: this.props.businessAreaList[0].businessAreaId });
                }
            }
        }
    };

    onFormSubmit = (formData) => {
        if (!this.isSaveAllowed()) {
            return;
        }
        if (this.props.action === "edit") {
            this.props.updateBusinessAreaData(this.props.businessAreaData, { ...formData });
        } else {
            let businessAreaData = { ...this.businessAreaDataDefault, ...formData };
            this.props.createBusinessAreaData(businessAreaData);
        }
    };

    getFormErrors = (fieldName) => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.props.formErrors[fieldName],
                validateStatus: "error",
            };
        }
        return this.emptyObject;
    };

    isReadOnlyView = () => {
        return this.state.readOnlyView || !(this.props.permission.canEdit || this.props.permission.canAdd);
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    };

    fileEvent = (e) => {
        return e && e.file;
    };

    readFileComplete = (fileReadResult) => {
        this.formRef.current.setFields([
            {
                name: "uploadFile",
                value: fileReadResult.file,
            },
        ]);
        this.setState({
            uploadFileExist: true,
        });
    };

    cancelUpload = (fileReadResult) => {
        this.formRef.current.setFields([
            {
                name: "uploadFile",
                value: null,
            },
        ]);
        this.setState({
            uploadFileExist: false,
        });
    };

    uploadFileExist = () => {
        if (this.formRef.current) {
            let uploadFile = this.formRef.current.getFieldValue(["uploadFile"]);
            if (uploadFile) {
                return true;
            }
        }
        return false;
    };

    onFieldsChange = (fields, allFields) => {
        if (this.formRef.current) {
            if (fields && fields.length > 0) {
                for (let field of fields) {
                    if (field.touched && field.name && field.name.length > 0) {
                        if (field.name[0] === "businessAreaId") {
                            this.formRef.current.setFields([
                                {
                                    name: "classifiers",
                                    value: [],
                                },
                            ]);
                        }
                    }
                }
            }
            let uploadFile = this.formRef.current.getFieldValue(["uploadFile"]);
            if (uploadFile) {
                this.setState({
                    uploadFileExist: true,
                });
            }
        }
    };

    onDomainDataPageSelect = (pagination) => {
        if (
            this.props.onFetchNextBusinessAreaDomainData &&
            this.props.businessAreaDomainDataResult.domainDataResult.pageKey &&
            pagination.current * pagination.pageSize >= this.props.businessAreaDomainDataResult.domainDataResult.Items.length
        ) {
            this.props.onFetchNextBusinessAreaDomainData(this.props.businessAreaData.dataDomainId, this.props.businessAreaDomainDataResult.domainDataResult.pageKey);
        }
    };

    render() {
        let businessAreaOptions = this.props.businessAreaList.map((businessArea) => ({ id: businessArea.businessAreaId, name: businessArea.name }));
        let dataDomains = businessAreaDataDomains.map((domain) => ({ value: domain }));
        return (
            <>
                <Form
                    labelCol={{ span: 3 }}
                    layout="horizontal"
                    labelAlign="left"
                    ref={this.formRef}
                    name="basic"
                    onFinish={this.onFormSubmit}
                    initialValues={this.businessAreaDataDefault}
                    onFieldsChange={this.onFieldsChange}
                    onValuesChange={this.props.onValuesChanged}
                    className="flex flex-col gap-4 *:mb-0"
                >
                    <Form.Item className="wrap-label" {...this.getFormErrors("businessAreaId")} label="Business Area" name={["businessAreaId"]}>
                        <Select {...(() => (this.isReadOnlyView() ? { open: false } : {}))()} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_BUSINESS_AREA_NAME_SELECT}>
                            {businessAreaOptions.map((item) => (
                                <Option key={item.id || Date.now()} value={item.id}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item className="wrap-label" {...this.getFormErrors("domainName")} label="Domain Name" name={["domainName"]} validateFirst={true} rules={nameRules}>
                        <AutoComplete
                            disabled={this.isReadOnlyView()}
                            options={dataDomains}
                            filterOption={(inputValue, option) => option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
                            data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DOMAIN_NAME_INPUT}
                        />
                    </Form.Item>
                    <Form.Item {...this.getFormErrors("domainDescription")} label="Description" name={["domainDescription"]} rules={descriptionRules}>
                        <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DESCRIPTION_INPUT} />
                    </Form.Item>
                    <Form.Item {...this.getFormErrors("domainPurpose")} label="Purpose" name={["domainPurpose"]} rules={descriptionRules}>
                        <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_PURPOSE_INPUT} />
                    </Form.Item>
                    <FormItem dependencies={["businessAreaId"]} label="Data Tags">
                        {({ getFieldValue }) => {
                            return (
                                <Form.Item {...this.getFormErrors("classifiers")} name={["classifiers"]} className="mb-0">
                                    <DataTagSelect
                                        businessAreaId={getFieldValue(["businessAreaId"])}
                                        disabled={this.isReadOnlyView()}
                                        mode="multiple"
                                        placeholder="Please select classifiers"
                                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DATA_TAGS_SELECT}
                                    ></DataTagSelect>
                                </Form.Item>
                            );
                        }}
                    </FormItem>
                    <div className={mergeClassNames("w-full flex-col gap-4", this.isReadOnlyView() ? "hidden" : "flex")}>
                        <Form.Item name={["uploadFile"]} valuePropName="file" noStyle>
                            <FileDragger
                                disabled={this.isReadOnlyView()}
                                onReadFileComplete={this.readFileComplete}
                                onCancelUpload={this.cancelUpload}
                                data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_FILE_INPUT}
                            ></FileDragger>
                        </Form.Item>
                        <div className="flex w-full gap-4 rounded-md bg-slate-50 p-4 *:mb-0">
                            {this.props.action !== "create" ? (
                                <Form.Item name={["dataUploadOptions", "uploadType"]} initialValue={"REPLACE_DATA"} className="pt-[5px]">
                                    <Radio.Group disabled={this.isReadOnlyView() || !this.state.uploadFileExist}>
                                        <Space direction="vertical">
                                            <Radio value="REPLACE_DATA" data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_REPLACE_DATA_OPTION}>
                                                Replace existing data in the dataset
                                            </Radio>
                                            <Radio value="APPEND_DATA" data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_APPEND_DATA_OPTION}>
                                                Append this data to the existing
                                            </Radio>
                                        </Space>
                                    </Radio.Group>
                                </Form.Item>
                            ) : (
                                <></>
                            )}
                            <Form.Item name={["dataUploadOptions", "firstRowHeader"]} valuePropName="checked" initialValue={true}>
                                <Checkbox
                                    disabled={this.isReadOnlyView() || !this.state.uploadFileExist}
                                    data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_FIRST_ROW_IS_HEADER_CHECKBOX}
                                >
                                    First row is header
                                </Checkbox>
                            </Form.Item>
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <Button onClick={this.props.onCancel} data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_CANCEL_BUTTON}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={this.isReadOnlyView() || !this.isSaveAllowed()}
                            data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_SAVE_BUTTON}
                        >
                            {this.state.uploadFileExist ? "Save and upload" : "Save"}
                        </Button>
                    </div>
                </Form>
                {this.props.businessAreaDomainDataResult && !this.props.businessAreaDomainDataResult.error ? (
                    <DomainDataPreviewList
                        recordCount={this.props.businessAreaData.recordCount || 0}
                        domainDataList={
                            this.props.businessAreaDomainDataResult && this.props.businessAreaDomainDataResult.domainDataResult
                                ? this.props.businessAreaDomainDataResult.domainDataResult.Items
                                : []
                        }
                        loading={!this.props.businessAreaDomainDataResult || this.props.businessAreaDomainDataResult.inProgress ? { tip: "Loading data..." } : false}
                        tableHeight={0}
                        onPageSelect={this.onDomainDataPageSelect}
                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_DOMAIN_DATA_PREVIEW_LIST}
                    ></DomainDataPreviewList>
                ) : (
                    <></>
                )}
            </>
        );
    }
}

export default ManageBusinessAreaData;
